import {
  createStyles,
  InputAdornment,
  makeStyles,
  MenuItem,
  Theme,
} from '@material-ui/core';
import { FilterOptionsState } from '@material-ui/lab';
import clsx from 'clsx';
import { Body, Button, Heading, IconButton } from 'copilot-design-system';
import { getIn, useFormikContext } from 'formik';
import React, { ChangeEvent, Fragment, useCallback, useMemo } from 'react';

import Autocomplete from 'src/components/Autocomplete';
import {
  OneOffLineItem,
  ProductLineItem,
} from 'src/legacy/components/billing/new/types';
import { Action, BaseActionsMenu } from 'src/legacy/components/Dropdowns';
import {
  GetAmountWithCommas,
  GetCurrencyPrefix,
  StripeCurrencyFormatterAdapter,
} from 'src/legacy/components/Formatters';
import { ArrowNEIcon, PlusIcon, TrashIcon } from 'src/legacy/components/Icons';
import { Edit } from 'src/legacy/components/Icons/EditIcon';
import { getRenderCurrencyPrefixProps } from 'src/legacy/components/Products/ProductEditPage/ProductEditPage';
import {
  GroupListboxComponent,
  RenderGroup,
} from 'src/legacy/components/Select/GroupListBox';
import {
  BaseTextField,
  CurrencyTextField,
  NumberTextField,
} from 'src/legacy/components/TextField';
import { LineItemEditorPricingMode } from 'src/routes/billing/components/LineItemEditorV2/LineItemEditor';
import { LineItemType } from 'src/services/api/invoicesApi';
import { BillingPeriodMapper, Price } from 'src/services/api/productsApi';
import { HoverBackground, NonHoverBorder, white } from 'src/theme/colors';
import { DropdownMenuShadow } from 'src/theme/shadows';

import { FilterOption } from '../types';

export const useLineItemStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    paper: {
      border: `1px solid ${NonHoverBorder}`,
      boxShadow: DropdownMenuShadow,
      '& .MuiAutocomplete-option': {
        paddingLeft: theme.spacing(2),
        '&[data-focus="true"]': {
          backgroundColor: HoverBackground,
        },
        // we want to add a bottom line divider to the
        // the "manage products" option item which is always
        // the first item in the dropdown list.
        '&[data-option-index="0"]': {
          borderBottom: 'none',
        },
        background: white,
        '&:hover': {
          backgroundColor: HoverBackground,
        },
      },
    },
  }),
);

export type LineItemFormSchema = OneOffLineItem | ProductLineItem;

export type PresentationProps<AllowRecurringPrices extends boolean> = {
  itemIndex: number;
  onRemoveLineItem: () => void;
  fieldName: string;
  openOnFocus: boolean;
  productDropdownOptions: FilterOption<AllowRecurringPrices>[];
  onLineItemSelection: (
    event: ChangeEvent<object>,
    value: string | FilterOption<AllowRecurringPrices> | null,
  ) => void;
  selectedProductItem: ProductItem;
  onFilterOptions: (
    options: FilterOption<AllowRecurringPrices>[],
    filterState: FilterOptionsState<FilterOption<AllowRecurringPrices>>,
  ) => FilterOption<AllowRecurringPrices>[];
  pricesDropdownOptions: Price[];
  onPriceChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  lineItem: LineItemFormSchema;
  allowRecurringPrices: AllowRecurringPrices;
  lineItemsInCents: boolean;
  lineItems: LineItemFormSchema[];
  formikAmountFieldName: string;
  onSaveItem: () => void;
  onDiscardItem: () => void;
  onClearItem: () => void;
  onEditItem: () => void;
  canBeRemoved: boolean;
  addProductRenderer: React.ReactNode;
  isSubmitting: boolean;
  mode: LineItemEditorPricingMode;
  allowCreatingNewProduct: boolean;
  allowCreatingNewPrice: boolean;
  defaultCurrency: string;
  isDragging?: boolean;
};

type ProductItem = {
  id: string;
  name: string;
};

const ADD_NEW_ONE_OFF_ITEM_ID = 'add-new-option-item';
const ADD_NEW_PRICE = 'add_new_price';
const NO_OPTION = 'no_option';

const NewProductOptionRenderer = ({ id, name }: ProductItem) => {
  if (id === ADD_NEW_ONE_OFF_ITEM_ID || id === 'add-new-product') {
    return (
      <div className="flex justify-start items-center overflow-hidden">
        <PlusIcon style={{ fontSize: 10 }} />
        <Body className="ml-2 overflow-hidden whitespace-nowrap overflow-ellipsis mr-5">
          {name ? `Add '${name}' as a new line item` : 'Add new line item'}
        </Body>
      </div>
    );
  }
  if (id === 'manage-products') {
    return (
      <div className="w-full flex flex-row justify-between items-center">
        <Body className="text-black text-sm">Manage products</Body>
        <ArrowNEIcon style={{ fontSize: 12 }} />
      </div>
    );
  }
  if (id === NO_OPTION) {
    return (
      // no-option is a disabled option, and thus gets 0.6 opacity
      // applied to it by MUI. We use a darker font to offset this.
      <Body className="text-black text-[12px] opacity-100">
        {`No matches found for "${name}"`}
      </Body>
    );
  }
  return (
    <Body className="overflow-hidden whitespace-nowrap overflow-ellipsis">
      {name}
    </Body>
  );
};

export function Presentation<AllowRecurringPrices extends boolean>({
  itemIndex,
  fieldName,
  productDropdownOptions,
  onLineItemSelection,
  selectedProductItem,
  onFilterOptions,
  pricesDropdownOptions,
  onPriceChange,
  onRemoveLineItem,
  lineItem,
  allowRecurringPrices,
  formikAmountFieldName,
  onSaveItem,
  onDiscardItem,
  onClearItem,
  onEditItem,
  openOnFocus,
  canBeRemoved,
  addProductRenderer: AddProductRenderer,
  isSubmitting,
  mode,
  allowCreatingNewProduct,
  allowCreatingNewPrice,
  defaultCurrency,
}: PresentationProps<AllowRecurringPrices>) {
  const classes = useLineItemStyles();
  const {
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();
  const productInputRef = React.useRef<HTMLInputElement>(null);
  const isEditingFieldName = `${fieldName}.isEditing`;

  const amountFieldName = `${fieldName}.${formikAmountFieldName}`;
  const descriptionFieldName = `${fieldName}.description`;
  const shouldAddPriceFieldName = `${fieldName}.shouldAddPrice`;

  const quantityFieldName = `${fieldName}.quantity`;
  const priceId = `${fieldName}.priceId`;
  const touchFieldsQuantity = getIn(touched, quantityFieldName);
  const errorQuantity = getIn(errors, quantityFieldName);

  const amountTouched = getIn(touched, amountFieldName);
  const amountErrors = getIn(errors, amountFieldName);
  const touchFieldsDescription = getIn(touched, descriptionFieldName);
  const errorDescription = getIn(errors, descriptionFieldName);
  const prefix = GetCurrencyPrefix({ currency: defaultCurrency });
  const accountForCents = (amount: number) => {
    return (amount / 100).toFixed(2);
  };
  const showAllProducts = mode === LineItemEditorPricingMode.All;

  const actions: Action[] = [
    {
      name: 'Edit item',
      icon: <Edit style={{ fontSize: 11 }} />,
      onClick: () => {
        onEditItem();
      },
    },
    {
      name: 'Delete item',
      icon: <TrashIcon style={{ fontSize: 11 }} />,
      onClick: () => {
        onRemoveLineItem();
      },
      isDelete: true,
    },
  ];

  const handleAddNewPrice = () => {
    setFieldValue(shouldAddPriceFieldName, true);
    setFieldValue(amountFieldName, '');
    setFieldTouched(amountFieldName, false);
    setFieldValue(priceId, '');
  };

  const handleCancelNewPrice = useCallback(() => {
    const firstPriceAmount = pricesDropdownOptions?.at(0)?.amount;
    if (firstPriceAmount) {
      const formattedAmount = (Number(firstPriceAmount) / 100).toString();
      setFieldValue(amountFieldName, formattedAmount);
    }
    setFieldValue(shouldAddPriceFieldName, false);
    setFieldValue(priceId, pricesDropdownOptions?.at(0)?.ref);
  }, [
    amountFieldName,
    defaultCurrency,
    pricesDropdownOptions,
    shouldAddPriceFieldName,
    priceId,
  ]);

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    if (lineItem.type === LineItemType.OneTime && lineItem.description) {
      setFieldValue(isEditingFieldName, true);
    }
  };

  const currencyInputProps = useMemo(() => {
    const currencyPrefixProps = getRenderCurrencyPrefixProps(prefix || '');
    if (lineItem.type !== LineItemType.OneTime && lineItem.shouldAddPrice) {
      return {
        ...currencyPrefixProps,
        endAdornment: (
          <InputAdornment position="end" className="mr-0.5">
            <IconButton
              className="p-0 cursor-pointer text-[16px]"
              onClick={handleCancelNewPrice}
              label=""
              icon="Close"
              variant="minimal"
            />
          </InputAdornment>
        ),
      };
    }
    return currencyPrefixProps;
  }, [lineItem, handleCancelNewPrice, prefix]);

  /**
   * Focus on product input when component mounts
   * autoFocus does not work with Autocomplete
   */
  React.useEffect(() => {
    if (productInputRef.current && openOnFocus && !lineItem.isEditing) {
      productInputRef.current.focus();
    }
  }, []);

  return (
    <Fragment key={lineItem.id}>
      <div className="w-full flex flex-col gap-3">
        {!lineItem.isValid ? (
          <div>
            <Autocomplete
              blurOnSelect={false}
              openOnFocus={
                (openOnFocus && !lineItem.isEditing) ||
                Boolean(
                  itemIndex === 0
                    ? false
                    : lineItem.type === LineItemType.OneTime,
                )
              }
              freeSolo={!showAllProducts}
              classes={{
                root: 'group',
                paper: classes.paper,
                listbox: 'px-0 py-[6px]',
                inputRoot:
                  ' h-[38px] [&>input]:text-[14px] [&>div>button>span]:text-black-heading [&>input]:font-normal',
                clearIndicator: clsx({
                  visible: canBeRemoved,
                  'invisible group-hover:visible':
                    showAllProducts && selectedProductItem.name,
                  invisible:
                    !canBeRemoved ||
                    (!selectedProductItem.name && showAllProducts),
                }),
                popupIndicator: 'hidden',
              }}
              onChange={onLineItemSelection}
              onInputChange={(_, __, reason) => {
                if (reason === 'clear') {
                  if (lineItem.isEditing) {
                    onClearItem();
                  } else {
                    onRemoveLineItem();
                  }
                }
              }}
              ListboxComponent={
                GroupListboxComponent as React.ComponentType<
                  React.HTMLAttributes<HTMLElement>
                >
              }
              renderGroup={RenderGroup}
              options={productDropdownOptions}
              getOptionLabel={(option) => option.name}
              getOptionDisabled={(option) => option.id === NO_OPTION}
              renderOption={(params) => (
                <NewProductOptionRenderer {...params} />
              )}
              inputValue={selectedProductItem.name}
              filterOptions={onFilterOptions}
              renderInput={(params) => (
                <BaseTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off',
                    isautocomplete: 'true',
                  }}
                  error={Boolean(touchFieldsDescription && errorDescription)}
                  helperText={
                    touchFieldsDescription && errorDescription
                      ? errorDescription
                      : ''
                  }
                  autoFocus={Boolean(
                    itemIndex === 0
                      ? false
                      : lineItem.type === LineItemType.OneTime,
                  )}
                  name={descriptionFieldName}
                  onBlur={handleOnBlur}
                  fullWidth
                  variant="outlined"
                  placeholder={
                    allowCreatingNewProduct
                      ? 'Find or add an item'
                      : 'Find product'
                  }
                  onChange={handleChange}
                  inputRef={productInputRef}
                />
              )}
              groupBy={(option) =>
                option.type === 'product' ? 'Existing products' : ''
              }
            />
          </div>
        ) : (
          <div className="flex justify-between items-center border border-non-hover-border border-solid rounded h-[46px] pl-3 pr-[5px]">
            <div className="text-[14px] font-normal line-clamp-2 overflow-hidden text-ellipsis">
              {lineItem.description}
            </div>
            <div className="flex gap-4 items-center">
              <div className="text-[14px] font-normal">
                {StripeCurrencyFormatterAdapter({
                  value:
                    Number(lineItem.quantity) * Number(lineItem.amount) * 100,
                  currency: defaultCurrency,
                })}
              </div>
              <BaseActionsMenu actions={actions} />
            </div>
          </div>
        )}

        {lineItem.isEditing && (
          <>
            <div className="w-full flex gap-2 items-baseline">
              <div className="w-1/2">
                <Heading className="mb-1 text-black-heading">Price</Heading>
                {lineItem.type === LineItemType.OneTime ||
                lineItem.shouldAddPrice ? (
                  <div>
                    <CurrencyTextField
                      fullWidth
                      error={Boolean(amountTouched && amountErrors)}
                      helperText={
                        amountTouched && amountErrors ? amountErrors : ''
                      }
                      name={amountFieldName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={lineItem.amount}
                      variant="outlined"
                      InputProps={currencyInputProps}
                      currency={defaultCurrency}
                      autoFocus={
                        lineItem.type === LineItemType.Product &&
                        lineItem.shouldAddPrice
                      }
                    />
                  </div>
                ) : (
                  <BaseTextField
                    sizeVariant="skinny"
                    fullWidth
                    select
                    name={priceId}
                    value={lineItem.priceId}
                    id="input-role"
                    onBlur={handleBlur}
                    onChange={(event) => {
                      // Skip updating the form field if the user selects the "Add New Price" option.
                      // This allows custom handling for adding a new price without overwriting the current field value.
                      const selectedValue = event.target.value;
                      if (selectedValue === ADD_NEW_PRICE) {
                        event.preventDefault();
                        return;
                      }
                      onPriceChange(event);
                    }}
                    variant="outlined"
                    additionalSelectProps={{
                      classes: {
                        root: 'text-[14px] !h-[38px] text-black-heading font-normal text-ellipsis overflow-hidden leading-[38px] !inline-block whitespace-nowrap ',
                        icon: clsx('h-4 w-4 mt-1.5 right-3', {
                          hidden:
                            pricesDropdownOptions.length <= 1 &&
                            !allowCreatingNewPrice,
                        }),
                      },
                      className: clsx(
                        'text-[14px] text-black-heading font-normal',
                        {
                          '[&>fieldset]:!border-non-hover-border':
                            pricesDropdownOptions.length <= 1 &&
                            !allowCreatingNewPrice,
                        },
                      ),
                    }}
                    disabled={
                      !allowCreatingNewPrice &&
                      pricesDropdownOptions.length <= 1
                    }
                  >
                    {pricesDropdownOptions.map((option) => {
                      const amountInDollars = accountForCents(option.amount);
                      return (
                        <MenuItem
                          key={option.ref}
                          value={option.ref}
                          className="px-3 py-1"
                        >
                          {prefix}
                          {GetAmountWithCommas(
                            amountInDollars,
                            defaultCurrency,
                          )}
                          {(allowRecurringPrices || showAllProducts) &&
                            option?.interval &&
                            ` / ${BillingPeriodMapper[option.interval]}`}
                        </MenuItem>
                      );
                    })}
                    {allowCreatingNewPrice && (
                      <MenuItem
                        value={ADD_NEW_PRICE}
                        onClick={handleAddNewPrice}
                        className="flex gap-2 items-centers px-3 py-1"
                      >
                        <PlusIcon style={{ fontSize: 12 }} />
                        Add new price
                      </MenuItem>
                    )}
                  </BaseTextField>
                )}
              </div>
              <div className="w-1/2">
                <Heading className="mb-1 text-[14px] font-medium text-black-heading">
                  Qty
                </Heading>
                <NumberTextField
                  className="[&>div>input]:text-[14px] [&>div>input]:font-normal [&>div>input]:!h-[38px]"
                  error={Boolean(touchFieldsQuantity && errorQuantity)}
                  fullWidth
                  helperText={
                    touchFieldsQuantity && errorQuantity ? errorQuantity : ''
                  }
                  name={quantityFieldName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={lineItem.quantity}
                  variant="outlined"
                />
              </div>
            </div>
            <div className="text-[14px] font-medium">
              Total:&nbsp;
              {StripeCurrencyFormatterAdapter({
                value:
                  Number(lineItem.quantity) * Number(lineItem.amount) * 100,
                currency: defaultCurrency,
              })}
            </div>
            {lineItem.type === LineItemType.OneTime &&
              allowCreatingNewProduct &&
              AddProductRenderer}
            <div className="flex gap-2 items-center">
              <Button
                onClick={onSaveItem}
                variant="primary"
                label="Save"
                className="w-20"
                loading={isSubmitting}
              />

              <Button
                onClick={onDiscardItem}
                label="Cancel"
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                variant="secondary"
                className="w-20"
              />
            </div>
          </>
        )}
      </div>
    </Fragment>
  );
}
